// Imports for external libraries go here.
import React, { FC, useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
import { useForm, FormProvider, SubmitHandler, FieldValues } from 'react-hook-form';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import clsx from 'clsx';
import axios from 'axios';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { Button, Types, Messages } from '@marriott/mi-ui-library';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { phoenixLegalContactUsTopicsAndSubTopicsQuery } from '@marriott/mi-legal-graphql';

import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';
import { LEGAL_PAGE_SPECIFIC_DATA_LAYER_SESSION_STORAGE_KEYS } from '../../utils/constants';
import { useAuthStore, useLegalGenericStore } from '../../store';
import { ContactInformation } from '../../molecules/ContactInformation/index';
import { HotelBookInformation } from '../../molecules/HotelBookInformation';
import { LegalTextWithLink } from '../../molecules/LegalTextWithLink';
import { HotelBookInformationCustomResetRef } from '../../molecules/HotelBookInformation/HotelBookInformation.types';
import { Dropdown } from '../../molecules/Dropdown';

import {
  ContactUsMainComponentProps,
  TopicValueType,
  ContactUSFormAEMModelType,
  ContactUSMainComponentUIStateType,
  initialContactUSMainComponentUIState,
  PhoenixLegalContactUsTopicsAndSubTopicsType,
  TopicListType,
} from './ContactUsForm.types';
import { CONTACT_INFORMATION_TOPICS, EXCLUDED_TOPICS_FOR_UNAUTH } from './constants';
import { StyledContactUsForm, GlobalStyles } from './ContactUsForm.styles';

const GET_TOPICS_AND_SUBTOPICS_OPERATION_NAME = 'phoenixLegalContactUsTopicsAndSubTopicsQuery';
const CONTACT_US_EMAIL_TEMPLATE_NAME = 'submit-contactus';

export const ContactUsFormConfig = {
  emptyLabel: 'ContactUsForm',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/contactusform`,
};

export const getTransformedSchemaForContactUsFormFromAEMModel = (
  model: ContactUSFormAEMModelType
): ContactUsMainComponentProps => {
  const {
    pageTitle,
    clearButtonText,
    chooseATopicLabel,
    submitButtonText,
    fieldRequiredText,
    topicLabel,
    dropDownPlaceholder,
    optionalLabel,
    contactInformation,
    hotelBookInformation,
    legalTextWithLink,
    apiErrorMessage,
    confirmationPageUrl,
  } = model;
  const transformedSchema: ContactUsMainComponentProps = {
    pageTitle,
    fieldRequiredText,
    chooseATopicLabel,
    submitButtonText,
    clearButtonText,
    topicLabel,
    dropDownPlaceholder,
    apiErrorMessage,
    confirmationPageUrl,
    components: {
      contactInformation: {
        ...contactInformation,
        optionFieldText: optionalLabel,
      },
      hotelBookInformation: {
        ...hotelBookInformation,
        optionFieldText: optionalLabel,
        // For now this is harcoded later these value will come from AEM model.json
        weekDaysShort: hotelBookInformation.weekDaysShort?.map(item => item.weekShortLabel),
        weekDaysLong: hotelBookInformation.weekDaysLong?.map(item => item.weekLongLabel),
        months: hotelBookInformation.months?.map(item => item.monthLabel),
      },
      legalTextWithLink,
    },
  };

  return transformedSchema;
};

// Use named rather than default exports.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ContactUsForm = (props: any) => {
  const transformedSchema = getTransformedSchemaForContactUsFormFromAEMModel(props.model);

  return (
    <EditableComponent config={ContactUsFormConfig} {...props}>
      <GlobalStyles />
      <ContactUsMainComponent {...transformedSchema} />
    </EditableComponent>
  );
};

export const ContactUsMainComponent: FC<ContactUsMainComponentProps> = props => {
  const {
    pageTitle,
    fieldRequiredText,
    chooseATopicLabel,
    apiErrorMessage,
    confirmationPageUrl,
    components,
    submitButtonText,
    clearButtonText,
    topicLabel,
    dropDownPlaceholder,
  } = props;

  const { isAuthenticated, sessionId } = useAuthStore();
  const { isAuthorMode, language } = useLegalGenericStore();
  const defaultTopic: TopicListType = useMemo(
    () => ({ label: dropDownPlaceholder, value: 'NA' }),
    [dropDownPlaceholder]
  );

  const [topics, setTopics] = useState<TopicListType[]>([defaultTopic]);
  const [selectedTopic, setSelectedTopic] = useState<TopicValueType>('NA');
  const router = useRouter();
  const { topic } = router.query;

  const {
    error: topicsError,
    data: topicData,
    loading: isTopicDataLoading,
  } = useQuery<PhoenixLegalContactUsTopicsAndSubTopicsType>(phoenixLegalContactUsTopicsAndSubTopicsQuery, {
    variables: {
      type: 'CONTACT_US_TOPICS',
    },
    context: generateApolloClientHeadersForUXLCalls(sessionId, GET_TOPICS_AND_SUBTOPICS_OPERATION_NAME, language),
    skip: isAuthorMode !== false,
  });

  const [contactUSMainComponentUIState, setContactUSMainComponentUIState] = useState<ContactUSMainComponentUIStateType>(
    initialContactUSMainComponentUIState
  );

  const { contactInformation, hotelBookInformation, legalTextWithLink } = components;

  const {
    isApiError,
    showHotelBookInformation,
    showContactInformationAndBtnContainer,
    showLegalTextWithLink,
    isApiCallStarted,
    isSubmitBtnDisabled,
  } = contactUSMainComponentUIState;

  const methods = useForm({
    mode: 'onSubmit',
    shouldUnregister: true,
  });

  const showAlertComponent = (shouldDisableSubmitBtn = false) => {
    setContactUSMainComponentUIState(prev => ({
      ...prev,
      isApiError: true,
      isApiCallStarted: false,
      isSubmitBtnDisabled: shouldDisableSubmitBtn,
    }));
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    if (topicsError) {
      showAlertComponent();
      console.log(`Something went wrong while fetching Topics: ${JSON.stringify(topicsError)}`);
      return;
    }
    const filteredTopicsWithOutEmptyKeys = topicData?.contentListsByType
      .filter(t => Boolean(t.key.trim()))
      .map(t => ({
        label: t.value,
        value: t.key as TopicValueType,
      }));

    setTopics([defaultTopic, ...(filteredTopicsWithOutEmptyKeys?.length ? filteredTopicsWithOutEmptyKeys : [])]);

    if (topic === 'FBR' && topicData) {
      setSelectedTopic(topic);
      setContactUSMainComponentUIState(prev => ({
        ...prev,
        showHotelBookInformation: true,
        showContactInformationAndBtnContainer: true,
      }));
    }
  }, [topicData, topicsError, topic, defaultTopic]);

  const hotelBookInformationRef = useRef<HotelBookInformationCustomResetRef>(null);

  const topicList = isAuthenticated
    ? topics
    : topics.filter(topic => !EXCLUDED_TOPICS_FOR_UNAUTH.includes(topic.value));

  const onSubmit: SubmitHandler<FieldValues> = async data => {
    setContactUSMainComponentUIState(prev => ({
      ...prev,
      isApiCallStarted: true,
    }));
    const emailAPIEndPoint = process.env['NEXT_PUBLIC_LEGAL_EMAIL_SERVICE_ENDPOINT'] ?? '/mi/v1/legal/submitContactUs';
    console.log(`Email api service end Point is : ${process.env['NEXT_PUBLIC_LEGAL_EMAIL_SERVICE_ENDPOINT']}`);
    data['target'] = selectedTopic;
    data['topic'] = topicList.find(t => t.value === selectedTopic)?.label;
    data['templateName'] = CONTACT_US_EMAIL_TEMPLATE_NAME;
    console.log(data);

    const headers = {
      'Content-Type': 'application/json',
      'Accept-Language': language,
    };
    axios.defaults.withCredentials = true;

    try {
      const response = await axios.post(emailAPIEndPoint, data, { headers });
      console.log('Response data:', response.data);

      const contactUsPageSpecificDataLayerKeys = {
        contact_topic: data['topic'],
        ...(data['hotelName'] && { contact_prop_marsha_code: data['hotelName'] }),
        ...(data['subTopicDescription'] && { contact_subtopic: data['subTopicDescription'] }),
      };

      sessionStorage.setItem(
        LEGAL_PAGE_SPECIFIC_DATA_LAYER_SESSION_STORAGE_KEYS,
        JSON.stringify(contactUsPageSpecificDataLayerKeys)
      );

      router.push(confirmationPageUrl || '/marriott/confirmation.mi');
    } catch (error) {
      console.log(`Something went wrong while sending mail: ${JSON.stringify(error)}`);
      showAlertComponent();
      scrollToTop();
    }
  };

  const resetForm = () => {
    const { getValues, reset } = methods;

    if (isAuthenticated) {
      const [firstName, lastName, email, marriottRewardsNumber] = getValues([
        'firstName',
        'lastName',
        'email',
        'marriottRewardsNumber',
      ]);
      reset({
        firstName,
        lastName,
        email,
        marriottRewardsNumber,
      });

      return;
    }
    reset();
  };

  const handleClear = (shouldResetFormRenderedUI: boolean) => {
    resetForm();
    shouldResetFormRenderedUI && hotelBookInformationRef.current?.resetFormRenderedUI?.();
    scrollToTop();
  };

  const handleTopicDropDownValueChange = (selectedTopic: TopicValueType) => {
    setSelectedTopic(selectedTopic);

    setContactUSMainComponentUIState(prev => ({
      ...prev,
      showHotelBookInformation: selectedTopic !== 'NA' && selectedTopic !== 'MSTA',
      showContactInformationAndBtnContainer: CONTACT_INFORMATION_TOPICS.includes(selectedTopic),
      showLegalTextWithLink: selectedTopic === 'MSTA',
    }));

    handleClear(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter') {
      const activeElement = document.activeElement as HTMLElement;
      if (
        activeElement &&
        (activeElement.tagName === 'INPUT' || activeElement.tagName === 'BUTTON') &&
        (activeElement as HTMLInputElement | HTMLButtonElement).type !== 'submit'
      ) {
        e.preventDefault();
      }
    }
  };

  const showHideContactInformationAndBtnContainer = (val: boolean) => {
    setContactUSMainComponentUIState(prev => ({
      ...prev,
      showContactInformationAndBtnContainer: val,
    }));
  };

  return (
    <StyledContactUsForm data-component-name="o-legal-ContactUsMainComponent" className="contactus_wrapper">
      {isApiError && <Messages messageType="error-sev1" className={clsx('mb-4')} messageText={apiErrorMessage} />}
      <h2>{pageTitle}</h2>
      <div className="contactus_container" data-testid="contactUs-component">
        <div className="main-container">
          <p>{fieldRequiredText}</p>
          <h4>{chooseATopicLabel}</h4>
          <Dropdown
            label={topicLabel}
            options={topicList}
            name="topic"
            className={clsx('contact-info-items topic-dropdown', { 'skeleton-loader': isTopicDataLoading })}
            value={selectedTopic}
            handleChange={selectedValue => handleTopicDropDownValueChange(selectedValue as TopicValueType)}
            id={'topic'}
          />
          {showLegalTextWithLink && <LegalTextWithLink {...legalTextWithLink} />}
          {showHotelBookInformation && (
            <FormProvider {...methods}>
              <form onSubmit={methods.handleSubmit(onSubmit)} onKeyDown={handleKeyDown} noValidate>
                <HotelBookInformation
                  ref={hotelBookInformationRef}
                  {...hotelBookInformation}
                  selectedTopic={selectedTopic}
                  isAuth={Boolean(isAuthenticated)}
                  subTopicDropDownPlaceholder={dropDownPlaceholder}
                  showHideContactAndBtnContainer={showHideContactInformationAndBtnContainer}
                  resetForm={resetForm}
                />
                {showContactInformationAndBtnContainer && (
                  <>
                    <ContactInformation
                      {...contactInformation}
                      selectedTopic={selectedTopic}
                      countryDropDownPlaceholder={dropDownPlaceholder}
                      isAuth={Boolean(isAuthenticated)}
                      className={clsx('mt-5')}
                      onApiError={showAlertComponent}
                    />
                    <div className="btn-container">
                      <hr className="mb-5" />
                      <Button
                        className={clsx('m-button-primary sbt-btn mr-4', { 'disable-btn': isSubmitBtnDisabled })}
                        type={Types.ButtonTypeVariation.Submit}
                        isDisabled={isApiCallStarted || isSubmitBtnDisabled}
                      >
                        {isApiCallStarted ? <div className="m-spinner-s"></div> : submitButtonText}
                      </Button>
                      <Button
                        className="m-button-secondary"
                        type={Types.ButtonTypeVariation.Reset}
                        callback={() => handleClear(true)}
                      >
                        {clearButtonText}
                      </Button>
                    </div>
                  </>
                )}
              </form>
            </FormProvider>
          )}
        </div>
      </div>
    </StyledContactUsForm>
  );
};
